import type { Project } from '@p2b/contract';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api.js';
import { useI18n } from '../../i18n.js';
import { Icon } from '../../ui.js';
import { checksText, type Checks } from './model.js';

export { deliveryRows, type Checks, type DeliveryRow, type DeliveryState } from './model.js';

/** Publications and (for staff) revisions of a project, shared by the header and the deliveries tab. */
export function useDeliveries(project: Project) {
  const caps = new Set(project.capabilities ?? []);
  const staff = caps.has('read:all');
  const pubs = useQuery({
    queryKey: ['publications', project.id],
    queryFn: () => api.publications(project.id),
    enabled: caps.has('read:published') || staff,
  });
  const revs = useQuery({
    queryKey: ['revisions', project.id],
    queryFn: () => api.revisions(project.id),
    enabled: staff,
  });
  const all = pubs.data ?? [];
  const active = all.filter((p) => !p.withdrawnAt);
  return { pubs, revs, staff, manage: caps.has('manage'), active, latest: active[0] ?? null };
}

/** Opens the deliver dialog from the header or a row (optionally with a revision picked). */
export const DeliverContext = createContext<(revisionId?: string) => void>(() => {});
export const useDeliver = () => useContext(DeliverContext);

/**
 * The model still: the picture `p2b revision create` renders from the viewer (or one staff added later). Without one,
 * or when it fails to load, a neutral drawing of the model symbol on a ground grid stands in, so the page has the same
 * shape with or without a picture.
 */
export function ModelStill({ src, className = '' }: { src?: string | null; className?: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (src && failed !== src)
    return (
      <img
        className={`model-still ${className}`}
        src={src}
        alt=""
        decoding="async"
        onError={() => setFailed(src)}
      />
    );
  return (
    <span className={`model-still placeholder ${className}`} aria-hidden="true">
      <svg viewBox="0 0 240 180" preserveAspectRatio="xMidYMid slice">
        <g className="model-still-grid">
          {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map((i) => (
            <g key={i}>
              <line
                x1={120 + i * 26 - 130}
                y1={118 + i * 15 - 75}
                x2={120 + i * 26 + 130}
                y2={118 + i * 15 + 75}
              />
              <line
                x1={120 - i * 26 - 130}
                y1={118 + i * 15 + 75}
                x2={120 - i * 26 + 130}
                y2={118 + i * 15 - 75}
              />
            </g>
          ))}
        </g>
        <g className="model-still-box">
          <path d="M120 64 146 79v30l-26 15-26-15V79z" />
          <path d="M94 79 120 94l26-15M120 94v30" />
        </g>
      </svg>
    </span>
  );
}

/** A modal dialog on the native <dialog> (focus trap, Escape and the backdrop close it). */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
  role = 'dialog',
}: {
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  role?: 'dialog' | 'alertdialog';
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`dlg${wide ? ' wide' : ''}`}
      role={role}
      aria-labelledby={id}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="dlg-box">
        <header className="dlg-head">
          <h2 id={id}>{title}</h2>
          <button type="button" className="icon-button" aria-label={t('close')} onClick={onClose}>
            <Icon.Close />
          </button>
        </header>
        <div className="dlg-body">{children}</div>
        {footer && <footer className="dlg-foot">{footer}</footer>}
      </div>
    </dialog>
  );
}

/** A ⋯ button with a small menu; any click inside the menu closes it. */
export function MoreMenu({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  return (
    <div className="more" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="icon-button"
        aria-label={t('moreActions')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon.More />
      </button>
      {open && (
        <div className="menu more-menu" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

export function ChecksTag({ checks }: { checks: Checks | null | undefined }) {
  const { t } = useI18n();
  if (!checks) return <span className="faint">—</span>;
  return (
    <span className={`tag ${checks.passed ? 'ok' : 'bad'}`}>
      {checks.passed && <Icon.Check width={14} height={14} />}
      {checksText(checks, t)}
    </span>
  );
}
